import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import {
  IcoGauge,
  IcoActivity,
  IcoBug,
  IcoPlug,
  IcoLife,
  IcoBook,
  IcoGrid,
  IcoLayers,
  IcoFile,
  IcoBuilding,
  IcoSpark,
  IcoCog,
  IcoChev,
  IcoUsers,
  IcoShield,
  IcoBell,
  IcoSlash,
  IcoLogout
} from './icons';
import { findProduct } from './products';

// Per-product nav. Each item gets an absolute `to` so React Router can match.
// Items with `children` expand inline (no nav change). `match` is the path
// prefix used to highlight the active item.
const NAV_BY_PRODUCT = {
  prevent: [
    { id: 'dashboard', label: 'Dashboard', Icon: IcoGauge, to: '/prevent', match: '/prevent' },
    { id: 'health', label: 'Vendor health', Icon: IcoActivity, to: '/vendor-health', match: '/vendor-health' },
    {
      id: 'vendors',
      label: 'Vendors',
      Icon: IcoBug,
      match: '/vendors',
      children: 'dynamic-vendors'
    },
    {
      id: 'integ',
      label: 'Integrations',
      Icon: IcoPlug,
      match: '/integrations',
      children: [
        { label: 'ServiceNow', to: '/integrations/servicenow' },
        { label: 'SSO (Single Sign-On)', to: '/integrations/sso' },
        { label: 'BugZero API', to: '/integrations/bugzero-api' },
        { label: 'Splunk', to: '/integrations/logging/splunk' }
      ]
    },
    {
      id: 'admin',
      label: 'Admin',
      Icon: IcoCog,
      match: '/users',
      children: [
        { label: 'Users', to: '/users/view-users' },
        { label: 'Groups', to: '/users/view-groups' },
        { label: 'Roles', to: '/users/view-roles' },
        { label: 'Firewall', to: '/admin/firewall' },
        { label: 'Global AI', to: '/admin/global-ai' }
      ]
    },
    {
      id: 'support',
      label: 'Support',
      Icon: IcoLife,
      match: '/support',
      children: [
        { label: 'Submit ticket', to: '/support/create-ticket' },
        { label: 'View tickets', to: '/support/view-tickets' },
        { label: 'Get help', to: '/support/get-help' }
      ]
    },
    {
      id: 'docs',
      label: 'Documentation',
      Icon: IcoBook,
      match: '/documentation',
      children: [
        { label: 'Integration Guides', to: '/documentation/sso-sn-integration-guides' },
        { label: 'User Guides', to: '/documentation/user-guides' },
        { label: 'Vendor Configuration Guides', to: '/documentation/vendor-configuration-guides' }
      ]
    }
  ],
  plan: [
    { id: 'projects', label: 'Projects', Icon: IcoGrid, to: '/plan', match: '/plan' },
    { id: 'docs', label: 'Documentation', Icon: IcoBook, to: '/documentation/user-guides', match: 'never' }
  ],
  predict: [
    { id: 'overview', label: 'Overview', Icon: IcoSpark, to: '/predict', match: '/predict' },
    { id: 'forecast', label: 'Forecast', Icon: IcoActivity, disabled: true },
    { id: 'models', label: 'Models', Icon: IcoLayers, disabled: true },
    { id: 'feedback', label: 'Send feedback', Icon: IcoLife, to: '/support/create-ticket' },
    { id: 'docs', label: 'Documentation', Icon: IcoBook, to: '/documentation/user-guides' }
  ]
};

// Platform nav. Settings sections are `status: 'coming-soon'` until implemented
// and are hidden from the sidebar (the route still resolves to a Coming Soon
// placeholder). Flip a section to `status: 'available'` to surface it here.
// Entries are kept (not deleted) so breadcrumb label lookup still resolves.
const PLATFORM_NAV = [
  { id: 'home', label: 'Home', Icon: IcoGrid, to: '/' },
  { id: 'organization', label: 'Organization', Icon: IcoBuilding, to: '/platform/organization', status: 'coming-soon' },
  { id: 'members', label: 'Members & Access', Icon: IcoUsers, to: '/platform/members', status: 'coming-soon' },
  { id: 'sso', label: 'SSO & Authentication', Icon: IcoShield, to: '/platform/sso', status: 'coming-soon' },
  { id: 'integrations', label: 'Integrations', Icon: IcoPlug, to: '/platform/integrations', status: 'coming-soon' },
  { id: 'notifications', label: 'Notifications', Icon: IcoBell, to: '/platform/notifications', status: 'coming-soon' },
  { id: 'audit', label: 'Audit log', Icon: IcoFile, to: '/platform/audit', status: 'coming-soon' },
  { id: 'firewall', label: 'Firewall', Icon: IcoSlash, to: '/platform/firewall', status: 'coming-soon' },
  { id: 'globalai', label: 'Global AI', Icon: IcoSpark, to: '/platform/globalai', status: 'coming-soon' },
  { id: 'billing', label: 'Billing & Plan', Icon: IcoFile, to: '/platform/billing', status: 'coming-soon' }
];

const SectionLabel = ({ children }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
      color: 'var(--ink-faint)',
      padding: '14px 18px 6px 18px'
    }}
  >
    {children}
  </div>
);
SectionLabel.propTypes = { children: PropTypes.node };

// ---- Vendor grouping (Prevent sidebar) -------------------------------------
// Vendors that share a `family` are collapsed into one expandable group; the
// child labels have the family prefix stripped (e.g. "AWS Security Hub" → "Security
// Hub" under the "AWS" group). A family with a single active vendor, or a vendor
// with no family, renders as a flat leaf using its full name.
const vendorTo = id => `/vendors/${id}/configuration`;

const normalizeVendorName = name => (name || '').replace('Systems', 'Networks');

const stripFamily = (name, family) => {
  const n = normalizeVendorName(name);
  if (!family) return n;
  const stripped = n.replace(new RegExp(`^${family}\\s*`, 'i'), '').trim();
  return stripped || n;
};

const buildVendorTree = vendors => {
  const families = new Map();
  const standalone = [];
  (vendors || []).forEach(v => {
    const fam = (v.family || '').trim();
    if (fam) {
      if (!families.has(fam)) families.set(fam, []);
      families.get(fam).push(v);
    } else {
      standalone.push(v);
    }
  });

  const nodes = [];
  families.forEach((members, fam) => {
    if (members.length === 1) {
      const v = members[0];
      nodes.push({ type: 'leaf', key: v.id, label: normalizeVendorName(v.name), to: vendorTo(v.id) });
    } else {
      const items = members
        .map(v => ({ key: v.id, label: stripFamily(v.name, fam), to: vendorTo(v.id) }))
        .sort((a, b) => a.label.localeCompare(b.label));
      nodes.push({ type: 'group', key: `fam:${fam}`, label: fam, items });
    }
  });
  standalone.forEach(v =>
    nodes.push({ type: 'leaf', key: v.id, label: normalizeVendorName(v.name), to: vendorTo(v.id) })
  );

  return nodes.sort((a, b) => a.label.localeCompare(b.label));
};

const VendorLeaf = ({ label, to, indent = 42 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const active = location.pathname.startsWith(to);
  return (
    <div
      onClick={() => navigate(to)}
      style={{
        padding: `5px 14px 5px ${indent}px`,
        fontSize: 'var(--fs-sm)',
        color: active ? 'var(--ink)' : 'var(--ink-soft)',
        fontWeight: active ? 600 : 500,
        cursor: 'pointer'
      }}
      onMouseEnter={e => {
        if (!active) e.currentTarget.style.color = 'var(--ink)';
      }}
      onMouseLeave={e => {
        if (!active) e.currentTarget.style.color = 'var(--ink-soft)';
      }}
    >
      {label}
    </div>
  );
};
VendorLeaf.propTypes = { label: PropTypes.string, to: PropTypes.string, indent: PropTypes.number };

const VendorGroup = ({ label, items }) => {
  const location = useLocation();
  const containsActive = items.some(c => location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(containsActive);
  return (
    <div>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '5px 14px 5px 42px',
          fontSize: 'var(--fs-sm)',
          color: 'var(--ink-soft)',
          fontWeight: 500,
          cursor: 'pointer'
        }}
        onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink)')}
        onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-soft)')}
      >
        <span className="bz-grow bz-truncate">{label}</span>
        <IcoChev size={12} style={{ transform: open ? 'rotate(180deg)' : '', transition: 'transform .15s' }} />
      </div>
      {open && items.map(c => <VendorLeaf key={c.key} label={c.label} to={c.to} indent={56} />)}
    </div>
  );
};
VendorGroup.propTypes = { label: PropTypes.string, items: PropTypes.array };

const SidebarItem = ({ item, accent, isActive, initiallyOpen, vendors }) => {
  const navigate = useNavigate();
  const Icon = item.Icon;
  const hasChildren = !!item.children;
  const [open, setOpen] = useState(!!initiallyOpen);

  const onClick = () => {
    if (item.disabled) return;
    if (hasChildren) setOpen(o => !o);
    else if (item.to) navigate(item.to);
  };

  const isVendorList = item.children === 'dynamic-vendors';
  const vendorTree = isVendorList ? buildVendorTree(vendors) : [];
  const resolvedChildren = Array.isArray(item.children) ? item.children : [];

  return (
    <div>
      <div
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '7px 14px 7px 16px',
          margin: '1px 8px',
          borderRadius: 6,
          cursor: item.disabled ? 'not-allowed' : 'pointer',
          color: item.disabled ? 'var(--ink-faint)' : isActive ? 'var(--ink)' : 'var(--ink-soft)',
          background: isActive ? 'var(--bg-tan)' : 'transparent',
          fontWeight: isActive ? 600 : 500,
          fontSize: 'var(--fs-md)',
          position: 'relative',
          transition: 'background .12s'
        }}
        onMouseEnter={e => {
          if (!isActive && !item.disabled) e.currentTarget.style.background = 'var(--bg-soft)';
        }}
        onMouseLeave={e => {
          if (!isActive && !item.disabled) e.currentTarget.style.background = 'transparent';
        }}
      >
        {isActive && (
          <span
            style={{
              position: 'absolute',
              left: -8,
              top: 6,
              bottom: 6,
              width: 3,
              borderRadius: 999,
              background: accent
            }}
          />
        )}
        <Icon size={15} />
        <span className="bz-grow bz-truncate">{item.label}</span>
        {item.disabled && <span style={{ fontSize: 10, color: 'var(--ink-faint)' }}>SOON</span>}
        {hasChildren && (
          <IcoChev size={13} style={{ transform: open ? 'rotate(180deg)' : '', transition: 'transform .15s' }} />
        )}
      </div>
      {hasChildren && open && (
        <div style={{ padding: '2px 0 6px 0' }}>
          {isVendorList
            ? vendorTree.map(node =>
                node.type === 'group' ? (
                  <VendorGroup key={node.key} label={node.label} items={node.items} />
                ) : (
                  <VendorLeaf key={node.key} label={node.label} to={node.to} />
                )
              )
            : resolvedChildren.map((c, i) => (
                <div
                  key={c.to || c.label || i}
                  onClick={() => c.to && navigate(c.to)}
                  style={{
                    padding: '5px 14px 5px 42px',
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--ink-soft)',
                    cursor: c.to ? 'pointer' : 'default'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-soft)')}
                >
                  {c.label}
                </div>
              ))}
        </div>
      )}
    </div>
  );
};
SidebarItem.propTypes = {
  item: PropTypes.object.isRequired,
  accent: PropTypes.string,
  isActive: PropTypes.bool,
  initiallyOpen: PropTypes.bool,
  vendors: PropTypes.array
};

const BugZeroSidebar = ({ contextId, platformSection, currentUser, vendors }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPlatform = contextId === 'platform';
  const product = !isPlatform ? findProduct(contextId) : null;
  const accent = isPlatform ? 'var(--bz-orange-1)' : product.accent;

  // Hide coming-soon items from the sidebar until they're implemented.
  const items = (isPlatform ? PLATFORM_NAV : NAV_BY_PRODUCT[contextId] || []).filter(i => i.status !== 'coming-soon');

  const isItemActive = item => {
    if (isPlatform) {
      if (item.id === 'home') return platformSection === 'home';
      return item.id === platformSection;
    }
    if (!item.match) return false;
    if (item.match === '/prevent') return location.pathname === '/prevent';
    return location.pathname.startsWith(item.match);
  };

  return (
    <aside
      style={{
        background: 'var(--bg-elev)',
        borderRight: '1px solid var(--line)',
        display: 'flex',
        flexDirection: 'column',
        width: 244,
        flexShrink: 0,
        height: '100vh',
        position: 'sticky',
        top: 0
      }}
    >
      <WorkspaceSwitcher contextId={contextId} platformSection={platformSection} />

      <div className="bz-scroll" style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        <SectionLabel>{isPlatform ? 'Platform' : product.short}</SectionLabel>
        {items.map(item => {
          const isActive = isItemActive(item);
          const initiallyOpen =
            // Vendors group: open when viewing any vendor page
            (item.children === 'dynamic-vendors' && location.pathname.startsWith('/vendors')) ||
            (Array.isArray(item.children) && item.children.some(c => location.pathname.startsWith(c.to || '')));
          return (
            <SidebarItem
              key={item.id}
              item={item}
              accent={accent}
              isActive={isActive}
              initiallyOpen={initiallyOpen}
              vendors={vendors}
            />
          );
        })}
      </div>

      <div
        style={{
          padding: '10px 12px',
          borderTop: '1px solid var(--line-soft)',
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 999,
            background: 'var(--bz-blue-1)',
            color: 'white',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: 11,
            flexShrink: 0
          }}
        >
          {(currentUser?.initials || 'BZ').slice(0, 2)}
        </div>
        <div className="bz-stack" style={{ lineHeight: 1.2, flex: 1, minWidth: 0 }}>
          <span className="bz-truncate" style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>
            {currentUser?.name || 'Signed in'}
          </span>
          <span className="bz-muted bz-truncate" style={{ fontSize: 'var(--fs-xs)' }}>
            {currentUser?.tenant || ''}
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate('/profile')}
          className="bz-btn bz-btn--ghost bz-btn--sm"
          title="Profile"
          style={{ padding: 6, flexShrink: 0 }}
        >
          <IcoCog size={14} />
        </button>
        <button
          type="button"
          onClick={() => window.location.assign(process.env.REACT_APP_LOGOUT_URL || '/authentication/logout')}
          className="bz-btn bz-btn--ghost bz-btn--sm"
          title="Log out"
          style={{ padding: 6, flexShrink: 0 }}
        >
          <IcoLogout size={14} />
        </button>
      </div>
    </aside>
  );
};

BugZeroSidebar.propTypes = {
  contextId: PropTypes.string.isRequired,
  platformSection: PropTypes.string,
  currentUser: PropTypes.object,
  vendors: PropTypes.array
};

export { PLATFORM_NAV };
export default BugZeroSidebar;
